<script lang="ts">
  import { showAlert } from '../../lib/ui-alert';
  import { onMount } from 'svelte';
  import SettingsLayout from '../../components/SettingsLayout.svelte';
  import BulkInviteModal from '../../components/BulkInviteModal.svelte';
  import Icon from '../../components/Icon.svelte';
  import {
    fetchAdminUsers,
    createInvitation,
    revokeInvitation,
    generatePasswordReset,
    updateUserRole,
    updateUserStatus,
    type AdminAuthUser,
    type PendingInvitation,
  } from '../../lib/admin-api';
  import { fmtDate, fmtDateTime, fmtRelative } from '../../lib/format';

  let users = $state<AdminAuthUser[]>([]);
  let pending = $state<PendingInvitation[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);

  let showInvite = $state(false);
  let showBulkInvite = $state(false);
  let iEmail = $state('');
  let iRole = $state('ANALYST');
  let busy = $state(false);

  /** Копі-модалка для accept / reset URL — одна на дві дії. */
  let copyModal = $state<{
    title: string;
    url: string;
    expiresAt: string;
  } | null>(null);
  let copyHint = $state<string | null>(null);

  async function load() {
    loading = true;
    err = null;
    try {
      const r = await fetchAdminUsers();
      users = r.users;
      pending = r.pendingInvitations;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  onMount(load);

  async function onInvite(e: SubmitEvent) {
    e.preventDefault();
    busy = true;
    try {
      const r = await createInvitation({ email: iEmail.trim(), role: iRole });
      showInvite = false;
      iEmail = '';
      copyModal = {
        title: 'Запрошення створено',
        url: r.acceptUrl,
        expiresAt: r.expiresAt,
      };
      await load();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  async function onRoleChange(u: AdminAuthUser, newRole: string) {
    if (!confirm(`Змінити роль ${u.email} на ${newRole}?`)) return;
    try {
      await updateUserRole(u.id, newRole);
      await load();
    } catch (e) {
      showAlert((e as Error).message);
    }
  }

  async function onToggleStatus(u: AdminAuthUser) {
    const next = u.status === 'active' ? 'disabled' : 'active';
    if (!confirm(`${next === 'disabled' ? 'Заблокувати' : 'Розблокувати'} ${u.email}?`)) return;
    try {
      await updateUserStatus(u.id, next);
      await load();
    } catch (e) {
      showAlert((e as Error).message);
    }
  }

  /**
   * Regenerate: revoke старого invitation + create new. Raw token старого
   * назавжди втрачено (БД зберігає лише hash), тож тут — єдиний спосіб
   * отримати свіжий URL.
   */
  async function onRegenerate(inv: PendingInvitation) {
    if (!confirm(`Згенерувати новий лінк для ${inv.email}? Старий стане недійсним.`)) return;
    busy = true;
    try {
      await revokeInvitation(inv.id);
      const r = await createInvitation({ email: inv.email, role: inv.role });
      copyModal = {
        title: 'Новий лінк-запрошення',
        url: r.acceptUrl,
        expiresAt: r.expiresAt,
      };
      await load();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  async function onRevoke(inv: PendingInvitation) {
    if (!confirm(`Скасувати запрошення ${inv.email}?`)) return;
    try {
      await revokeInvitation(inv.id);
      await load();
    } catch (e) {
      showAlert((e as Error).message);
    }
  }

  async function onResetPassword(u: AdminAuthUser) {
    if (u.status !== 'active') {
      showAlert('Спочатку розблокуй користувача (status = active).');
      return;
    }
    if (!confirm(`Згенерувати reset-link для ${u.email}?`)) return;
    busy = true;
    try {
      const r = await generatePasswordReset(u.id);
      copyModal = {
        title: '🔑 Reset link для ' + u.email,
        url: r.resetUrl,
        expiresAt: r.expiresAt,
      };
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  function copyUrl(url: string) {
    void navigator.clipboard.writeText(url).then(() => {
      copyHint = 'Скопійовано';
      setTimeout(() => (copyHint = null), 2000);
    });
  }
</script>

<SettingsLayout active="users">
  <div class="mx-auto max-w-[1200px]">
    <header class="mb-5 flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h1 class="text-xl font-semibold leading-tight">Команда</h1>
        <p class="mt-1 text-[12px] text-[var(--color-text-muted)]">
          Реєстрація через цю систему неможлива — лише запрошення
        </p>
      </div>
      <div class="flex gap-2">
        <button
          onclick={() => (showBulkInvite = true)}
          class="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-pu)] text-[var(--color-pu-3)] px-3 py-1.5 text-xs font-semibold hover:bg-purple-50"
        >
          <Icon name="copy" size={14} /> Масове запрошення
        </button>
        <button
          onclick={() => (showInvite = !showInvite)}
          class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-xs font-semibold text-white"
        >
          + Запросити користувача
        </button>
      </div>
    </header>

    {#if showInvite}
      <form onsubmit={onInvite} class="rounded-xl border border-[var(--color-border-3)] bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <label class="block flex-1 min-w-[200px]">
          <div class="text-[10.5px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Email</div>
          <input bind:value={iEmail} type="email" required class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs" />
        </label>
        <label class="block">
          <div class="text-[10.5px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Роль</div>
          <select bind:value={iRole} class="rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs">
            <option value="ADMIN">ADMIN</option>
            <option value="ANALYST">ANALYST</option>
            <option value="READER">READER</option>
          </select>
        </label>
        <button type="submit" disabled={busy} class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
          {busy ? 'Створення…' : 'Створити invitation'}
        </button>
      </form>
    {/if}

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else}
      {#if pending.length > 0}
        <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4 mb-4">
          <div class="text-[13px] font-bold mb-2">Очікують прийняття ({pending.length})</div>
          <div class="flex flex-col gap-1.5">
            {#each pending as inv (inv.id)}
              <div class="flex items-center gap-3 rounded-md bg-[var(--color-bg-3)] px-3 py-2 text-xs">
                <span class="font-mono text-[11px]">{inv.email}</span>
                <span class="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700">{inv.role}</span>
                <span class="text-[10.5px] text-[var(--color-text-muted)]">
                  від {inv.invitedByEmail} · до {fmtDate(inv.expiresAt)}
                </span>
                <div class="ml-auto flex items-center gap-1">
                  <button
                    onclick={() => onRegenerate(inv)}
                    disabled={busy}
                    title="Згенерувати новий лінк (старий стане недійсним)"
                    class="inline-flex items-center gap-1 rounded border border-[var(--color-border-3)] bg-white px-2 py-0.5 text-[10.5px] font-semibold hover:bg-[var(--color-pu-light)] disabled:opacity-50"
                  ><Icon name="url" size={12} /> Новий лінк</button>
                  <button
                    onclick={() => onRevoke(inv)}
                    title="Скасувати запрошення"
                    class="inline-flex items-center gap-1 rounded border border-red-200 bg-white px-2 py-0.5 text-[10.5px] font-semibold text-red-600 hover:bg-red-50"
                  ><Icon name="delete" size={12} /> Скасувати</button>
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}

      <div class="overflow-hidden rounded-lg border border-[var(--color-border-3)]">
        <table class="w-full text-xs">
          <thead class="bg-[var(--color-bg-3)]">
            <tr class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)]">
              <th class="text-left px-3 py-2 font-bold">Email</th>
              <th class="text-left px-3 py-2 font-bold">Роль</th>
              <th class="text-left px-3 py-2 font-bold">Статус</th>
              <th class="text-left px-3 py-2 font-bold">Last login</th>
              <th class="text-left px-3 py-2 font-bold">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {#each users as u (u.id)}
              <tr class="border-t border-[var(--color-border-3)]">
                <td class="px-3 py-2">{u.email}</td>
                <td class="px-3 py-2">
                  <select value={u.role} onchange={(e) => onRoleChange(u, (e.target as HTMLSelectElement).value)} class="rounded-md border border-[var(--color-border-3)] bg-white px-2 py-0.5 text-[11px] font-bold">
                    <option value="ADMIN">ADMIN</option>
                    <option value="ANALYST">ANALYST</option>
                    <option value="READER">READER</option>
                  </select>
                </td>
                <td class="px-3 py-2">
                  {#if u.status === 'active'}
                    <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">active</span>
                  {:else}
                    <span class="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">disabled</span>
                  {/if}
                </td>
                <td class="px-3 py-2 text-[11px] text-[var(--color-text-muted)]">{fmtRelative(u.lastLoginAt)}</td>
                <td class="px-3 py-2 text-[11px] text-[var(--color-text-muted)]">{fmtDate(u.createdAt)}</td>
                <td class="px-3 py-2">
                  <div class="flex items-center gap-1 justify-end">
                    {#if u.status === 'active'}
                      <button
                        onclick={() => onResetPassword(u)}
                        disabled={busy}
                        title="Згенерувати reset-link"
                        class="rounded border border-[var(--color-border-3)] px-2 py-0.5 text-[10.5px] font-semibold hover:bg-[var(--color-pu-light)] disabled:opacity-50"
                      >🔑 Reset password</button>
                    {/if}
                    <button onclick={() => onToggleStatus(u)} class="rounded border border-[var(--color-border-3)] px-2 py-0.5 text-[10.5px] font-semibold hover:bg-[var(--color-bg-3)]">
                      {u.status === 'active' ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <!-- Permissions matrix -->
      <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4 mt-4">
        <div class="text-[13px] font-bold mb-2">Матриця прав</div>
        <table class="w-full text-xs">
          <thead><tr><th class="text-left py-1.5 w-2/5">Дія</th><th class="text-center py-1.5">ADMIN</th><th class="text-center py-1.5">ANALYST</th><th class="text-center py-1.5">READER</th></tr></thead>
          <tbody class="text-[var(--color-text-muted)]">
            <tr class="border-t border-[var(--color-border-3)]"><td class="py-1.5">Перегляд дашбордів</td><td class="text-center text-emerald-600">✓</td><td class="text-center text-emerald-600">✓</td><td class="text-center text-emerald-600">✓</td></tr>
            <tr class="border-t border-[var(--color-border-3)]"><td class="py-1.5">Перегляд PII (email/phone)</td><td class="text-center text-emerald-600">✓</td><td class="text-center text-emerald-600">✓</td><td class="text-center">маска</td></tr>
            <tr class="border-t border-[var(--color-border-3)]"><td class="py-1.5">Export CSV</td><td class="text-center text-emerald-600">✓</td><td class="text-center text-emerald-600">✓</td><td class="text-center text-red-600">×</td></tr>
            <tr class="border-t border-[var(--color-border-3)]"><td class="py-1.5">Конфіг воронок / продуктів</td><td class="text-center text-emerald-600">✓</td><td class="text-center text-red-600">×</td><td class="text-center text-red-600">×</td></tr>
            <tr class="border-t border-[var(--color-border-3)]"><td class="py-1.5">Запросити / змінити роль</td><td class="text-center text-emerald-600">✓</td><td class="text-center text-red-600">×</td><td class="text-center text-red-600">×</td></tr>
            <tr class="border-t border-[var(--color-border-3)]"><td class="py-1.5">Reset password інших</td><td class="text-center text-emerald-600">✓</td><td class="text-center text-red-600">×</td><td class="text-center text-red-600">×</td></tr>
            <tr class="border-t border-[var(--color-border-3)]"><td class="py-1.5">Audit log</td><td class="text-center text-emerald-600">✓</td><td class="text-center text-red-600">×</td><td class="text-center text-red-600">×</td></tr>
          </tbody>
        </table>
      </div>
    {/if}
  </div>

  <!-- Copy-link модалка (єдина для invitation + reset) -->
  {#if copyModal}
    {@const m = copyModal}
    <div
      class="fixed inset-0 bg-black/40 grid place-items-center z-50 p-6"
      onclick={() => (copyModal = null)}
      onkeydown={(e) => e.key === 'Escape' && (copyModal = null)}
      role="presentation"
    >
      <div
        class="bg-white rounded-2xl w-full max-w-2xl shadow-2xl p-5"
        onclick={(e) => e.stopPropagation()}
        role="dialog"
        tabindex="-1"
      >
        <div class="flex items-center justify-between mb-3">
          <div class="text-base font-bold">{m.title}</div>
          <button class="inline-flex items-center text-[var(--color-text-muted)]" onclick={() => (copyModal = null)}><Icon name="close" size={18} /></button>
        </div>
        <div class="text-[12px] text-[var(--color-text-muted)] mb-2">
          Скопіюй і передай користувачу у месенджер:
        </div>
        <div class="rounded-md border border-[var(--color-border-3)] bg-[var(--color-bg-3)] px-3 py-2 mb-2">
          <code class="text-[11.5px] font-mono break-all">{m.url}</code>
        </div>
        <div class="flex items-center gap-2 mb-3">
          <button
            onclick={() => copyUrl(m.url)}
            class="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white"
          ><Icon name="copy" size={13} /> Скопіювати</button>
          {#if copyHint}
            <span class="text-[11px] text-emerald-700 font-semibold">{copyHint}</span>
          {/if}
          <span class="ml-auto text-[10.5px] text-[var(--color-text-muted)]">
            Діє до: {fmtDateTime(m.expiresAt)}
          </span>
        </div>
        <div class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          Лінк показується ОДИН раз. Якщо втратив — натисни «Новий лінк» у списку pending
          (старий стане недійсним).
        </div>
        <div class="flex justify-end mt-4">
          <button
            onclick={() => (copyModal = null)}
            class="rounded-md border border-[var(--color-border-3)] px-4 py-1.5 text-[11px] font-semibold"
          >Закрити</button>
        </div>
      </div>
    </div>
  {/if}
</SettingsLayout>

{#if showBulkInvite}
  <BulkInviteModal
    onClose={() => (showBulkInvite = false)}
    onDone={() => { showBulkInvite = false; void load(); }}
  />
{/if}
